<template>
  <div class="toolbar">
    <div class="toolbar-left">
      <my-pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @pageChanged="onPage"
      />
      <span class="total">Всего: <strong>{{ totalItems }}</strong></span>
    </div>

    <div class="toolbar-right">
      <select class="hl-select" :value="sortPresetValue" @change="onSortPreset">
        <option disabled value="manual">Ручная сортировка</option>
        <option value="dayCity">Погрузка (день) → Откуда → Куда</option>
        <option value="platformDayCity">Платформа → Погрузка (день) → Откуда → Куда</option>
        <option value="platformTime">Платформа → Погрузка (время)</option>
        <option value="fromTo">Откуда → Куда</option>
        <option value="closingUrgency">Срочность закрытия</option>
      </select>

      <div class="vdiv" />

      <button class="btn-filter" @click="copyPage">
        Копировать страницу
      </button>

      <button class="btn-filter" :disabled="exportLoading" @click="exportToExcel">
        {{ exportLoading ? 'Готовим...' : 'Excel' }}
      </button>

      <button
        class="btn-filter settings-toggle"
        :class="{ active: settingsOpen }"
        :aria-expanded="settingsOpen"
        aria-controls="distribution-table-settings"
        @click="settingsOpen = !settingsOpen"
      >
        Настройки таблицы
        <span class="accordion-chevron" :class="{ open: settingsOpen }" aria-hidden="true" />
      </button>

      <button class="btn-filter" @click="$emit('toggleFilter')">
        {{ filterOpen ? 'Скрыть фильтры' : 'Фильтры' }}
      </button>
    </div>

    <transition name="settings-accordion">
      <div v-if="settingsOpen" id="distribution-table-settings" class="settings-panel">
        <div class="settings-heading">
          <div>
            <div class="settings-title">Столбцы таблицы</div>
            <div class="settings-hint">
              Перетаскивайте карточки или используйте стрелки для изменения порядка. Ширину колонок меняйте перетаскиванием границы в заголовке таблицы.
            </div>
          </div>
          <div class="settings-actions">
            <button class="btn-filter" @click="$emit('resetSettings')">Сбросить</button>
            <button class="btn-filter" @click="settingsOpen = false">Готово</button>
          </div>
        </div>
        <TableScaleControl :modelValue="scale" @update:modelValue="$emit('scaleChanged', $event)" />
        <div class="hl-settings-row">
          <label class="settings-label">Подсветка</label>
          <select class="hl-select" :value="highlightMode" @change="onHighlightMode">
            <option value="none">Без подсветки</option>
            <option value="platform">Платформа</option>
            <option value="tariff">Тип тарифа</option>
            <option value="clientCategory">ТОП/КА</option>
            <option value="closingStatus">Статус закрытия</option>
            <option value="departureCity">Город отправки</option>
            <option value="departureDay">День отправки</option>
            <option value="urgency">Срочность</option>
            <option value="status">Статус</option>
            <option value="simple">Простой</option>
          </select>
          <select class="hl-select" :value="highlightPalette" @change="onPalette" v-if="showPalette">
            <option value="colorful">Цветная</option>
            <option value="zebra">Зебра</option>
            <option value="bee">Пчела</option>
          </select>
        </div>
        <draggable class="settings-grid" v-model="settingsOrder" item-key="id" handle=".drag-handle" @change="emitOrder">
          <template #item="{ element, index }">
            <div class="settings-row">
              <button class="drag-handle" type="button" :aria-label="`Перетащить столбец ${element.label}`" title="Перетащить">
                <span class="drag-grip" aria-hidden="true" />
              </button>
              <label class="visibility-label">
                <input type="checkbox" :checked="isVisible(element.id)" :disabled="isLastVisible(element.id)" @change="toggleVisibility(element.id)">
                <span>{{ element.label }}</span>
              </label>
              <span class="sort-indicator">{{ sortIndicator(element.id) }}</span>
              <button type="button" class="move-btn" :disabled="index === 0" @click="move(index, -1)" aria-label="Выше">↑</button>
              <button type="button" class="move-btn" :disabled="index === settingsOrder.length - 1" @click="move(index, 1)" aria-label="Ниже">↓</button>
            </div>
          </template>
        </draggable>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import TableScaleControl from '@/components/UI/TableScaleControl.vue';
import { DAY_CITY_SORTS, PLATFORM_DAY_CITY_SORTS, PLATFORM_TIME_SORTS, FROM_TO_SORTS, CLOSING_URGENCY_SORTS, formatOrderSummary } from '../helpers';
import { apiSortsToTanstack, COLUMN_IDS } from '../distributionState.mjs';

const PRESETS = { dayCity: DAY_CITY_SORTS, platformDayCity: PLATFORM_DAY_CITY_SORTS, platformTime: PLATFORM_TIME_SORTS, fromTo: FROM_TO_SORTS, closingUrgency: CLOSING_URGENCY_SORTS };
const COLUMN_LABELS = {
  critical: '!', status: 'Статус', distr_id: 'Номер',
  'departure.planed_date': 'Погрузка', closing_urgency: 'Закрытие',
  'departure.city_name': 'Откуда', 'destination.city_name': 'Куда',
  cargo: 'Груз', 'order.veh.type': 'ТС', tariff: 'Тариф', payment: 'Оплата',
  display_name: 'Платформа', client_category: 'ТОП/КА', 'order.meta.client_name': 'Клиент',
  worker: 'Исполнитель', actions: 'Действия',
};

function detectSortPreset(sorts) {
  if (!sorts || !sorts.length) return 'manual';
  const str = JSON.stringify(sorts);
  for (const [key, value] of Object.entries(PRESETS)) {
    if (JSON.stringify(apiSortsToTanstack(value)) === str) return key;
  }
  return 'manual';
}

export default {
  name: 'DistributionToolbar',
  components: { draggable, TableScaleControl },
  props: {
    totalPages: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
    filterOpen: { type: Boolean, default: false },
    highlightMode: { type: String, default: 'none' },
    highlightPalette: { type: String, default: 'colorful' },
    orders: { type: Array, default: () => [] },
    sorting: { type: Array, default: () => [] },
    columnOrder: { type: Array, default: () => COLUMN_IDS },
    columnVisibility: { type: Object, default: () => ({}) },
    exportLoading: { type: Boolean, default: false },
    scale: { type: String, default: 'normal' },
    totalItems: { type: Number, default: 0 },
  },
  emits: ['toggleFilter', 'pageChanged', 'highlightChanged', 'sortingChanged', 'columnOrderChanged', 'columnVisibilityChanged', 'resetSettings', 'export', 'scaleChanged'],
  setup(props, { emit }) {
    const settingsOpen = ref(false);
    const settingsOrder = ref([]);
    const syncOrder = () => {
      settingsOrder.value = props.columnOrder.map((id) => ({ id, label: COLUMN_LABELS[id] || id }));
    };
    watch(() => props.columnOrder, syncOrder, { immediate: true, deep: true });
    const sortPresetValue = computed(() => detectSortPreset(props.sorting));
    const showPalette = computed(() => props.highlightMode !== 'none' && !['urgency', 'status', 'clientCategory', 'closingStatus'].includes(props.highlightMode));

    const onPage = (page) => emit('pageChanged', page);
    const onHighlightMode = (e) => emit('highlightChanged', { highlightMode: e.target.value });
    const onPalette = (e) => emit('highlightChanged', { highlightPalette: e.target.value });
    const onSortPreset = (e) => {
      if (PRESETS[e.target.value]) emit('sortingChanged', apiSortsToTanstack(PRESETS[e.target.value]));
    };

    const copyPage = () => {
      if (!props.orders.length) return;
      navigator.clipboard?.writeText(props.orders.map(formatOrderSummary).join('\n')).catch(() => {});
    };

    const exportToExcel = () => emit('export');
    const emitOrder = () => emit('columnOrderChanged', settingsOrder.value.map((item) => item.id));
    const move = (index, direction) => {
      const target = index + direction;
      if (target < 0 || target >= settingsOrder.value.length) return;
      const next = [...settingsOrder.value];
      [next[index], next[target]] = [next[target], next[index]];
      settingsOrder.value = next;
      emitOrder();
    };
    const isVisible = (id) => props.columnVisibility[id] !== false;
    const visibleCount = computed(() => COLUMN_IDS.filter(isVisible).length);
    const isLastVisible = (id) => isVisible(id) && visibleCount.value === 1;
    const toggleVisibility = (id) => {
      if (isLastVisible(id)) return;
      emit('columnVisibilityChanged', { ...props.columnVisibility, [id]: !isVisible(id) });
    };
    const sortIndicator = (id) => {
      let index = props.sorting.findIndex((item) => item.id === id);
      if (index < 0 && id === 'departure.planed_date') {
        index = props.sorting.findIndex((item) => item.id === 'departure.planed_date_day');
      }
      if (index < 0) return '';
      return `${props.sorting[index].desc ? '↓' : '↑'}${index + 1}`;
    };

    return { onPage, onHighlightMode, onPalette, onSortPreset, sortPresetValue, copyPage, exportToExcel, showPalette, settingsOpen, settingsOrder, emitOrder, move, isVisible, isLastVisible, toggleVisibility, sortIndicator };
  },
};
</script>

<style scoped>
.toolbar {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 8px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.settings-panel {
  flex: 0 0 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid #d9e0e8;
  border-radius: 6px;
  background: #f8fafc;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.settings-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.settings-title { font-weight: 700; color: #344054; }
.settings-hint { margin-top: 2px; color: #667085; font-size: 12px; }
.settings-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 6px; }
.settings-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  min-height: 36px;
  padding: 3px 6px;
  border: 1px solid #e1e6ed;
  border-radius: 5px;
  background: #fff;
}
.settings-row:hover { border-color: #b9c8da; }
.drag-handle, .move-btn { border: 0; background: transparent; cursor: pointer; color: #667085; }
.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 28px;
  padding: 0;
  cursor: grab;
}
.drag-handle:active { cursor: grabbing; }
.drag-grip {
  width: 12px;
  height: 18px;
  background-image: radial-gradient(circle, currentColor 1.4px, transparent 1.6px);
  background-size: 6px 6px;
}
.drag-handle:hover { color: #007bff; }
.move-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #d0d5dd;
  border-radius: 4px;
  background: #fff;
  font-size: 15px;
  transition: color 120ms ease, border-color 120ms ease, background-color 120ms ease;
}
.move-btn:hover:not(:disabled) {
  border-color: #007bff;
  background: #eef6ff;
  color: #007bff;
}
.move-btn:disabled { opacity: 0.35; cursor: default; }
.visibility-label { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; cursor: pointer; }
.visibility-label span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; user-select: none; }
.sort-indicator { width: 26px; color: #007bff; font-weight: 700; }
.settings-actions { display: flex; justify-content: flex-end; gap: 8px; }
.settings-toggle { display: inline-flex; align-items: center; gap: 8px; }
.settings-toggle.active { background: #eef6ff; }
.accordion-chevron {
  width: 7px;
  height: 7px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
  transition: transform 160ms ease;
}
.accordion-chevron.open { transform: translateY(2px) rotate(225deg); }
.settings-accordion-enter-active,
.settings-accordion-leave-active { transition: opacity 140ms ease, transform 140ms ease; }
.settings-accordion-enter-from,
.settings-accordion-leave-to { opacity: 0; transform: translateY(-4px); }

.hl-select {
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
  color: #333;
  height: 30px;
  box-sizing: border-box;
}

.btn-filter {
  padding: 4px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #fff;
  color: #007bff;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
  height: 30px;
  box-sizing: border-box;
  white-space: nowrap;
  user-select: none;
}

.settings-panel label,
.drag-handle,
.move-btn { user-select: none; }

.btn-filter:hover {
  background: #007bff;
  color: #fff;
}

.btn-filter:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.vdiv {
  width: 1px;
  height: 16px;
  background: #d7dce2;
  flex-shrink: 0;
}

.total { padding-left: 4px; color: #667085; font-size: 13px; white-space: nowrap; }
.hl-settings-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.settings-label { font-size: 13px; color: #344054; white-space: nowrap; }

@media (max-width: 900px) {
  .toolbar-left,
  .toolbar-right { width: 100%; }
  .toolbar-right { justify-content: flex-start; }
  .settings-heading { align-items: stretch; flex-direction: column; }
  .settings-actions { justify-content: flex-start; }
}

@media screen and (max-width: 426px) {
  .total {
    white-space: wrap;
    max-width: min-content;
  }
}
@media screen and (max-width: 769px) {
  .toolbar-right {
    flex-wrap: nowrap;
    overflow-x: scroll;
  }
}
</style>
